import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import LearnHub from "@/pages/LearnHub";
import LearnTopic from "@/pages/LearnTopic";
import Concierge from "@/pages/Concierge";
import Reserve from "@/pages/Reserve";
import ReserveSuccess from "@/pages/ReserveSuccess";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AdminUsers from "@/pages/AdminUsers";
import AdminIBCLeads from "@/pages/AdminIBCLeads";
import Scripts from "@/pages/Scripts";
import Studio from "@/pages/Studio";
import CloserLab from "@/pages/CloserLab";
import Quiz from "@/pages/Quiz";
import SignUp from "@/pages/SignUp";
import Playbook from "@/pages/Playbook";
import GoalLab from "@/pages/GoalLab";
import AdminScenarios from "@/pages/AdminScenarios";
import AdminPipeline from "@/pages/AdminPipeline";
import AdminAudits from "@/pages/AdminAudits";
import AdminCallbacks from "@/pages/AdminCallbacks";
import AdminProfiles from "@/pages/AdminProfiles";
import AdminPdfExports from "@/pages/AdminPdfExports";
import AdminClarity from "@/pages/AdminClarity";
import ClarityScore from "@/pages/ClarityScore";
import TaxBuckets from "@/pages/TaxBuckets";
import CheckIn from "@/pages/CheckIn";
import RefLanding from "@/pages/RefLanding";
import AdminRetention from "@/pages/AdminRetention";
import AdminRollover from "@/pages/AdminRollover";
import RolloverShare from "@/pages/RolloverShare";
import Credentials from "@/pages/Credentials";
import Calculator from "@/pages/Calculator";
import Results from "@/pages/Results";
import { SeriesHub, SeriesDetail } from "@/pages/Series";
import WealthImplementation from "@/pages/WealthImplementation";
import InfiniteBanking from "@/pages/InfiniteBanking";
import PolicyAudit from "@/pages/PolicyAudit";
import QuizInsights from "@/pages/QuizInsights";
import Privacy from "@/pages/Privacy";
import Support from "@/pages/Support";
import ExitIntent from "@/components/ExitIntent";
import VisitorBanner from "@/components/VisitorBanner";
import AuthGate from "@/components/AuthGate";
import { auth } from "@/lib/api";
import { requestTrackingPermissionOnce } from "@/lib/tracking";

function Protected({ children }) {
  if (!auth.isLoggedIn()) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  // Kick off Apple ATT permission request on native iOS launch.
  // No-op on web; safe to call unconditionally.
  React.useEffect(() => {
    requestTrackingPermissionOnce();
  }, []);
  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#141414",
            border: "1px solid #262626",
            color: "#F3F4F6",
            borderRadius: "0",
            fontFamily: "Outfit, sans-serif",
          },
        }}
      />
      <AuthGate>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/learn" element={<LearnHub />} />
          <Route path="/learn/:slug" element={<LearnTopic />} />
          <Route path="/quiz" element={<Quiz />} />
          <Route path="/clarity-score" element={<ClarityScore />} />
          <Route path="/tax-buckets" element={<TaxBuckets />} />
          <Route path="/check-in" element={<CheckIn />} />
          <Route path="/r/:code" element={<RefLanding />} />
          <Route path="/rollover/s/:id" element={<RolloverShare />} />
          <Route path="/series" element={<SeriesHub />} />
          <Route path="/series/:id" element={<SeriesDetail />} />
          <Route path="/wealth-implementation" element={<WealthImplementation />} />
          <Route path="/infinite-banking" element={<InfiniteBanking />} />
          <Route path="/policy-audit" element={<PolicyAudit />} />
          <Route path="/credentials" element={<Credentials />} />
          <Route path="/calculator" element={<Calculator />} />
          <Route path="/results" element={<Results />} />
          <Route path="/concierge" element={<Concierge />} />
          <Route path="/reserve" element={<Reserve />} />
          <Route path="/reserve/success" element={<ReserveSuccess />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/goals" element={<GoalLab />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/dashboard/profiles" element={<Protected><AdminProfiles /></Protected>} />
          <Route path="/dashboard/admin" element={<Protected><AdminUsers /></Protected>} />
          <Route path="/dashboard/wealth-for-life" element={<Protected><AdminIBCLeads /></Protected>} />
          <Route path="/dashboard/scenarios" element={<Protected><AdminScenarios /></Protected>} />
          <Route path="/dashboard/pipeline" element={<Protected><AdminPipeline /></Protected>} />
          <Route path="/dashboard/audits" element={<Protected><AdminAudits /></Protected>} />
          <Route path="/dashboard/callbacks" element={<Protected><AdminCallbacks /></Protected>} />
          <Route path="/dashboard/retention" element={<Protected><AdminRetention /></Protected>} />
          <Route path="/dashboard/rollover" element={<Protected><AdminRollover /></Protected>} />
          <Route path="/dashboard/clarity" element={<Protected><AdminClarity /></Protected>} />
          <Route path="/dashboard/pdf-exports" element={<Protected><AdminPdfExports /></Protected>} />
          <Route path="/dashboard/playbook" element={<Protected><Playbook /></Protected>} />
          <Route path="/dashboard/quiz" element={<Protected><QuizInsights /></Protected>} />
          <Route path="/dashboard/scripts" element={<Protected><Scripts /></Protected>} />
          <Route path="/dashboard/studio" element={<Protected><Studio /></Protected>} />
          <Route path="/closer-lab" element={<Protected><CloserLab /></Protected>} />
          <Route path="/dashboard/closer-lab" element={<Protected><CloserLab /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthGate>
      <ExitIntent />
      <VisitorBanner />
    </BrowserRouter>
  );
}

export default App;
